import { Popover, Transition } from "@headlessui/react";
import {
  offset,
  useFloating,
  shift,
  autoUpdate,
  arrow
} from "@floating-ui/react-dom";
import React, { useRef, useState } from "react";
import Exit from "../../assets/Exit.svg";
import cx from "classnames";
import css from "./Tooltip.module.css";

interface Props {
  tooltipTitle: React.ReactElement | string;
  tooltipLabel: React.ReactElement | string;
  tooltipContent: React.ReactElement | string;
  buttonClassName?: string;
}

const Tooltip = ({
  tooltipTitle,
  tooltipContent,
  tooltipLabel,
  buttonClassName
}: Props) => {
  const [isShowing, setIsShowing] = useState(false);
  const arrowRef = useRef(null);

  const {
    x,
    y,
    refs,
    placement,
    strategy,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }
  } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: "bottom-end",
    strategy: "absolute",
    middleware: [offset(15), shift(), arrow({ element: arrowRef })]
  });

  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  }[placement.split("-")[0]] as string;

  const arrowStyles = {
    left: arrowX != null ? `${arrowX - 40}px` : "",
    top: arrowY != null ? `${arrowY}px` : "",
    [staticSide]: "-4px"
  };

  const arrowClassnames = cx(css.arrow, "w-[10px] h-[10px] absolute", {
    "after:rotate-[225deg]": staticSide === "bottom",
    "after:rotate-45": staticSide === "top"
  });

  const toggleShow = () => {
    setIsShowing(prev => !prev);
  };

  return (
    <Popover className="relative z-10 inline-block translate-y-[4px]">
      <Popover.Button
        ref={refs.setReference}
        className={buttonClassName}
        onClick={toggleShow}>
        {tooltipLabel}
      </Popover.Button>

      <Transition
        show={isShowing}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0">
        <div
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ? x + 40 : 0
          }}
          ref={refs.setFloating}>
          <Popover.Panel
            id="toolip"
            className="w-[85vw] md:w-[600px] inline-flex items-center rounded-md p-6 bg-coinage-white text-coinage-black pr-8 border-[1px] border-coinage-stroke">
            <div
              className={arrowClassnames}
              ref={arrowRef}
              style={arrowStyles}
              id="arrow"
            />
            <button
              onClick={toggleShow}
              className="absolute right-4 top-4 z-10">
              <Exit width="24" />
            </button>
            <div className="flex flex-col gap-2 max-w-[90vw] text-left">
              <h4 className="subhead2 text-coinage-black">{tooltipTitle}</h4>
              <p className="body2 text-coinage-body"> {tooltipContent}</p>
            </div>
          </Popover.Panel>
        </div>
      </Transition>
    </Popover>
  );
};

export default Tooltip;
